import React, { useState } from 'react';
import type { IvPatientInvisalignBundle } from '../../patient-invisalign/types';
import type { PatientRecord } from '../../patients/types';
import { cn } from '../../lib/cn';
import { Avatar } from '../Avatar/Avatar';
import { Button } from '../Button/Button';
import { useToast } from '../Toast/Toast';
import { InvStageChip } from '../Invisalign/InvShell';
import { InvLogModal } from '../Invisalign/InvLogModal';
import { InvSmilePicker, InvSmileTiles } from '../Invisalign/InvSmilePicker';
import { Modal } from '../Modal/Modal';
import { Icon } from '../Icon/Icon';
import {
  INV_DONT,
  INV_LOG_CONCERNS,
  INV_LOG_EVENTS,
  INV_PATIENT,
  INV_PROGRAMME,
  INV_STAGE_META,
} from '../../invisalign/mockData';
import type { InvStage } from '../../invisalign/types';

export interface PatientInvisalignTabContentProps {
  patient: PatientRecord;
  bundle: IvPatientInvisalignBundle;
  onBookConsult?: () => void;
  /** Opens the programme page (amendment 33 §3) on a step, patient in preview. */
  onPreviewStep?: ((step: string) => void) | undefined;
}

/** The #1 question → which email goes next (spec 45 §2, spec 35 §14). */
const CONCERN_EMAIL: Record<string, { day: number; subject: string }> = {
  Cost: { day: 11, subject: 'What it costs, and what your plan covers' },
  Insurance: { day: 11, subject: 'What it costs, and what your plan covers' },
  'Monthly payments': { day: 11, subject: 'What it costs, and what your plan covers' },
  'Tried braces before': { day: 17, subject: 'Aligners or braces? An honest comparison' },
  'Worried about results': { day: 17, subject: 'Aligners or braces? An honest comparison' },
  'How long it takes': { day: 24, subject: 'How long would clear aligners take?' },
  Discomfort: { day: 31, subject: 'What happens at an aligner consult' },
  'Eating and cleaning': { day: 38, subject: 'Eating, cleaning, and the everyday questions' },
  'How it looks': { day: 45, subject: 'A question we get a lot: will people notice?' },
};

/** The "His worry" line in What-to-say, by #1 question. */
const WORRY_LINE: Record<string, string> = {
  'How it looks': 'How it looks at work — the trays are clear, most people don’t notice them.',
  Cost: 'What it costs — a consult gives the exact number, and there are monthly plans.',
  Insurance: 'What insurance covers — his plan has unused orthodontic benefit.',
  'Monthly payments': 'Paying monthly — it doesn’t have to be paid all at once.',
  Discomfort: 'Whether it hurts — some pressure for a day or two with each set, then it fades.',
  'How long it takes': 'How long it takes — it depends on his teeth; a consult gives the plan.',
  'Eating and cleaning': 'Eating and cleaning — trays come out to eat, then go back in.',
  'Tried braces before': 'He’s tried braces before — aligners work differently, taken out to eat.',
  'Worried about results': 'Worried about results — the consult shows the plan step by step.',
};

const STEP_ORDER: { stage: InvStage; label: string }[] = [
  { stage: 'worth-asking', label: 'Worth asking' },
  { stage: 'learning', label: 'Learning' },
  { stage: 'ready', label: 'Ready' },
  { stage: 'consult-booked', label: 'Consult booked' },
  { stage: 'accepted', label: 'Accepted' },
];

/**
 * Patients › Invisalign (amendment 30 §6). Rebuilt around one mock patient
 * (p001 Orville Allison), reading INV_PATIENT. Staff set the "About him" facts
 * by tapping; every change rewrites "What to say". Book consult and Log the
 * conversation sit in the top bar and open the shared, taps-only modal.
 */
export function PatientInvisalignTabContent({ patient, onBookConsult, onPreviewStep }: PatientInvisalignTabContentProps) {
  const { toast } = useToast();
  // The tab reads the patient in the URL (spec 45 §4). Only Orville (p001) is an
  // aligner prospect in the mock; anyone else gets the empty state until marked.
  const isOrville = patient.id === 'p001' || patient.name === 'Orville Allison';
  const [marked, setMarked] = useState(false);
  const p = INV_PATIENT;
  const [logOpen, setLogOpen] = useState(false);
  const [logMode, setLogMode] = useState<'before' | 'after'>('after');
  const [smile, setSmile] = useState<string[]>(p.smileProfile);
  const [concerns, setConcerns] = useState<string[]>(p.concerns);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDraft, setPickerDraft] = useState<string[]>(p.smileProfile);
  const [whoDecides, setWhoDecides] = useState(p.whoDecides);
  const [event, setEvent] = useState<string | null>(p.comingUpEvent ?? null);
  const [bestTime, setBestTime] = useState(p.bestTime);
  const [emailsPaused, setEmailsPaused] = useState(false);
  const [textsStatus, setTextsStatus] = useState(p.textsConsent.status);

  // The #1 question is the first concern; the rest are "also asked about" (§2).
  const topConcern = concerns[0] ?? null;
  const alsoConcerns = concerns.slice(1);
  const setTop = (c: string) => {
    setConcerns(c === topConcern ? concerns : [c, ...concerns.filter((x) => x !== c)]);
    toast({ title: 'Saved — #1 question', variant: 'success' });
  };
  const toggleAlso = (c: string) => {
    setConcerns(alsoConcerns.includes(c) ? concerns.filter((x) => x !== c) : [...concerns, c]);
    toast({ title: 'Saved — also asked about', variant: 'success' });
  };

  // A patient who isn't an aligner prospect (mock: Todd, p002).
  if (!isOrville && !marked) {
    return (
      <div className="pp-fade mx-auto w-full max-w-[1080px]">
        <div className="mt-6 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-8 text-center shadow-[var(--shadow-card)]">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[var(--text-muted)]">
            <Icon kind="gem" size={22} stroke={1.8} />
          </span>
          <div className="mt-3 text-[15px] font-semibold text-[var(--text-primary)]">{patient.first} isn’t an aligner prospect</div>
          <div className="mx-auto mt-1 max-w-[460px] text-[12.5px] leading-[1.6] text-[var(--text-muted)]">
            ELVA hasn’t found an interest signal for {patient.first}, and nobody has asked him.
          </div>
          <Button
            variant="primary"
            size="sm"
            className="mt-4"
            onClick={() => { setMarked(true); toast({ title: `${patient.first} is in Worth asking`, variant: 'success' }); }}
          >
            Mark as worth asking
          </Button>
          <div className="mt-2 text-[11.5px] text-[var(--text-muted)]">Adds {patient.first} to Worth asking, with the signal “Added by Ava Thompson”.</div>
        </div>
      </div>
    );
  }

  // Just marked (not Orville): the Worth-asking view with Record the answer.
  if (!isOrville) {
    return (
      <div className="pp-fade mx-auto w-full max-w-[1080px]">
        <div className="mb-4 flex items-center justify-end gap-2">
          <Button variant="primary" size="sm" iconLeft="phone" onClick={() => { setLogMode('before'); setLogOpen(true); }}>Record the answer</Button>
        </div>
        <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-card)]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-[var(--font-display)] text-[18px] font-semibold text-[var(--text-primary)]">{patient.name}</span>
            <InvStageChip stage="worth-asking" />
          </div>
          <div className="mt-1 text-[12.5px] text-[var(--text-secondary)]">In Worth asking, with the signal “Added by Ava Thompson”. Record the answer when someone asks {patient.first} about aligners.</div>
        </div>
        <InvLogModal
          open={logOpen}
          patientName={patient.name}
          mode="before"
          stage="worth-asking"
          onClose={() => { setLogOpen(false); }}
          onSave={(sentence) => { setLogOpen(false); toast({ title: `Logged — ${sentence}`, variant: 'success' }); }}
        />
      </div>
    );
  }

  const stepIdx = STEP_ORDER.findIndex((s) => s.stage === p.stage);
  // The #1 question decides which email goes next (spec 45 §2 / spec 35 §14).
  const nextByConcern = topConcern ? CONCERN_EMAIL[topConcern] : undefined;
  const nextSubject = nextByConcern?.subject ?? p.emails.nextSubject;
  const nextDay = nextByConcern?.day ?? p.emails.nextDay;
  const nextMsg = INV_PROGRAMME.find((m) => m.day === nextDay);
  const nextStepId = nextMsg ? nextMsg.id : 'email-1';

  const saveTap = (label: string) => { toast({ title: `Saved — ${label}`, variant: 'success' }); };

  return (
    <div className="pp-fade mx-auto w-full max-w-[1080px]">
      {/* top bar */}
      <div className="mb-4 flex items-center justify-end gap-2">
        <Button variant="secondary" size="sm" iconLeft="calendar" onClick={onBookConsult}>Book consult</Button>
        <Button variant="primary" size="sm" iconLeft="phone" onClick={() => { setLogOpen(true); }}>Log the conversation</Button>
      </div>

      {/* header */}
      <div className="flex flex-wrap items-start gap-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
        <Avatar name={p.name} hue="var(--brand-indigo)" size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-[var(--font-display)] text-[18px] font-semibold text-[var(--text-primary)]">{p.name}</span>
            <InvStageChip stage={p.stage} />
          </div>
          <div className="mt-0.5 text-[12.5px] text-[var(--text-secondary)]">{p.headerLine}</div>
        </div>
        <div className="flex gap-4">
          <Figure v={p.orthoBenefit} l={`Ortho benefit · ${p.orthoBenefitNote}`} money />
          <Figure v={`${String(p.emails.opened)} of ${String(p.emails.sent)}`} l="Emails opened" />
          <Figure v={`Day ${String(p.emails.day)}`} l={`of ${String(p.emails.of)}`} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-[1.7fr_1fr] gap-4 max-[980px]:grid-cols-1">
        <div className="flex flex-col gap-4">
          {/* where he is */}
          <Card>
            <H>Where he is</H>
            <div className="flex items-center gap-1">
              {STEP_ORDER.map((s, i) => (
                <React.Fragment key={s.stage}>
                  <div className={cn('flex-1 rounded-full py-1 text-center text-[11px] font-semibold',
                    i === stepIdx ? 'text-[var(--warn-amber)]' : i < stepIdx ? 'text-[var(--money-green)]' : 'text-[var(--text-muted)]')}
                    style={{ background: i === stepIdx ? 'var(--warn-amber-fill)' : i < stepIdx ? 'var(--money-green-fill)' : 'var(--surface-sunken)' }}>
                    {s.label}
                  </div>
                  {i < STEP_ORDER.length - 1 ? <span className="text-[var(--text-muted)]">›</span> : null}
                </React.Fragment>
              ))}
            </div>
            <div className="mt-3 rounded-[10px] border border-[color-mix(in_srgb,var(--warn-amber)_30%,transparent)] bg-[var(--warn-amber-fill)] px-3.5 py-2.5">
              <div className="text-[12.5px] font-semibold text-[var(--text-primary)]">{p.readySince}</div>
              <div className="mt-0.5 text-[12px] text-[var(--text-secondary)]">He {p.readyWhy}</div>
              <div className="mt-1.5 text-[12px] font-semibold text-[var(--warn-amber)]">{p.nextStepNote}</div>
            </div>
            <div className="mt-2 flex gap-3 text-[12px]">
              <button type="button" className="font-semibold text-[var(--text-secondary)] hover:underline" onClick={() => { toast({ title: 'Parked for 6 months', variant: 'warning' }); }}>Park 6 months</button>
              <button type="button" className="font-semibold text-[var(--alert-red)] hover:underline" onClick={() => { toast({ title: 'Closed', variant: 'warning' }); }}>Close</button>
            </div>
          </Card>

          {/* what to say — the "His worry" line follows the #1 question (§2); it's
              dropped when no #1 is picked. */}
          <Card>
            <H>What to say</H>
            <div className="flex flex-col gap-2.5">
              {p.whatToSay
                .filter((w) => w.label !== 'His worry' || topConcern !== null)
                .map((w) => (
                  <div key={w.label} className="grid grid-cols-[90px_1fr] gap-2 text-[12.5px]">
                    <span className="font-semibold text-[var(--text-muted)]">{w.label}</span>
                    <span className="text-[var(--text-secondary)]">
                      {w.label === 'His worry' && topConcern ? (WORRY_LINE[topConcern] ?? topConcern) : w.body}
                    </span>
                  </div>
                ))}
            </div>
            <div className="mt-3 rounded-[8px] bg-[var(--warn-amber-fill)] px-3 py-1.5 text-[11.5px] text-[var(--text-secondary)]">{INV_DONT}</div>
          </Card>

          {/* about him */}
          <Card>
            <H>About him <span className="font-normal text-[var(--text-muted)]">— staff set these, they rewrite “What to say”</span></H>
            <AboutRow title="Smile profile" source="set by Dana">
              {smile.length > 0 ? (
                <div className="flex w-full flex-wrap items-center gap-2">
                  <InvSmileTiles picked={smile} />
                  <button type="button" onClick={() => { setPickerDraft(smile); setPickerOpen(true); }} className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Change</button>
                </div>
              ) : (
                <div className="flex w-full items-center gap-2">
                  <span className="text-[12px] text-[var(--text-muted)]">Not set</span>
                  <Button variant="secondary" size="sm" onClick={() => { setPickerDraft([]); setPickerOpen(true); }}>Pick his smile</Button>
                </div>
              )}
            </AboutRow>
            <AboutRow title="His #1 question" source="one · decides the next email">
              <ChipRow items={INV_LOG_CONCERNS} selected={topConcern ? [topConcern] : []} onToggle={(v) => { setTop(v); }} single tag="#1" />
            </AboutRow>
            <AboutRow title="Also asked about" source="any">
              <ChipRow items={INV_LOG_CONCERNS.filter((c) => c !== topConcern)} selected={alsoConcerns} onToggle={(v) => { toggleAlso(v); }} />
            </AboutRow>
            <AboutRow title="Who else decides" source="one">
              <ChipRow items={['Just him', 'Partner', 'Parent']} selected={[whoDecides]} onToggle={(v) => { setWhoDecides(v); saveTap('who decides'); }} single />
            </AboutRow>
            <AboutRow title="Coming up" source="event + month">
              <ChipRow items={INV_LOG_EVENTS} selected={event ? [event] : []} onToggle={(v) => { setEvent(event === v ? null : v); saveTap('coming up'); }} single />
              {p.comingUpMonth !== undefined ? <span className="ml-1 self-center text-[11.5px] text-[var(--text-muted)]">· {p.comingUpMonth}</span> : null}
            </AboutRow>
            <AboutRow title="Best time to call" source="one">
              <ChipRow items={['Morning', 'Afternoon', 'Evening']} selected={[bestTime]} onToggle={(v) => { setBestTime(v); saveTap('best time'); }} single />
            </AboutRow>
            <AboutRow title="Texts about aligners" source="set by the patient">
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold',
                      textsStatus === 'yes' ? 'bg-[#EEF6F2] text-[#1B5F45]' : textsStatus === 'no' ? 'bg-[var(--surface-sunken)] text-[var(--text-muted)]' : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
                    )}
                  >
                    {textsStatus === 'yes' ? 'Yes' : textsStatus === 'no' ? 'No' : 'Not asked yet'}
                  </span>
                  <span className="text-[11.5px] text-[var(--text-muted)]">{p.textsConsent.detail}</span>
                  {textsStatus === 'yes' ? (
                    <Button variant="secondary" size="sm" onClick={() => { setTextsStatus('no'); toast({ title: 'Aligner texts turned off', variant: 'warning' }); }}>Turn off</Button>
                  ) : null}
                </div>
                <span className="text-[11px] text-[var(--text-muted)]">Staff can switch this off, never on — the patient turns it on themselves.</span>
              </div>
            </AboutRow>
          </Card>

          {/* why he's a prospect + the ask */}
          <Card>
            <H>Why he’s a prospect</H>
            <div className="flex flex-col gap-1.5">
              {p.whyProspect.map((s, i) => (
                <div key={`${s.label}-${String(i)}`} className="flex items-baseline justify-between gap-3 text-[12.5px]">
                  <span className="text-[var(--text-secondary)]">{s.label}</span>
                  {s.date !== undefined ? <span className="shrink-0 font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{s.date}</span> : null}
                </div>
              ))}
            </div>
            <div className="mt-3 border-t border-[var(--border-default)] pt-2.5">
              <div className="pc-eyebrow text-[var(--text-muted)]">The ask</div>
              <div className="mt-1 text-[12.5px] text-[var(--text-secondary)]">{p.askBy} — <span className="font-semibold text-[var(--text-primary)]">“{p.askAnswer}”</span></div>
            </div>
          </Card>
        </div>

        {/* right rail */}
        <div className="flex flex-col gap-4">
          <Card>
            <H>ELVA’s messages</H>
            <div className="text-[12.5px] text-[var(--text-secondary)]">{p.emails.sent} emails · {p.emails.textsSent ?? 0} texts sent · {p.emails.opened} opened</div>
            <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">Emails and texts together{textsStatus === 'yes' ? ' — he said yes to texts' : ''}.</div>
            {p.emails.clickedNote !== undefined ? <div className="mt-0.5 text-[12px] font-semibold text-[var(--warn-amber)]">{p.emails.clickedNote}</div> : null}
            <div className="mt-2.5 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">Next · Day {nextDay}</div>
              <div className="mt-0.5 text-[12.5px] font-semibold text-[var(--text-primary)]">{nextSubject}</div>
              <div className="mt-1 text-[11.5px] text-[var(--text-muted)]">{p.emails.nextHeldNote}</div>
              <div className="mt-2 flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => { if (onPreviewStep) { onPreviewStep(nextStepId); } else { toast({ title: 'Preview', variant: 'success' }); } }}>Preview</Button>
                <Button variant={emailsPaused ? 'primary' : 'secondary'} size="sm" onClick={() => { setEmailsPaused(!emailsPaused); toast({ title: emailsPaused ? 'Messages resumed' : 'Messages paused', variant: emailsPaused ? 'success' : 'warning' }); }}>{emailsPaused ? 'Resume' : 'Pause'}</Button>
              </div>
            </div>
          </Card>

          <Card>
            <H>Invis-Line</H>
            {p.lineConvos.map((c, i) => (
              <div key={`${c.at}-${String(i)}`} className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3 text-[12px]">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">{c.at}</div>
                <div className="mt-1 text-[var(--text-primary)]"><span className="font-semibold text-[var(--text-muted)]">Q · </span>{c.q}</div>
                <div className="mt-1 leading-[1.5] text-[var(--text-secondary)]"><span className="font-semibold text-[var(--brand-indigo)]">ELVA · </span>{c.a}</div>
                {c.reply !== undefined ? <div className="mt-1 italic text-[var(--text-muted)]">↳ {c.reply}</div> : null}
              </div>
            ))}
          </Card>

          <Card>
            <H>History</H>
            <div className="flex flex-col">
              {p.history.map((h, i) => (
                <div key={`${h.at}-${String(i)}`} className="border-t border-[var(--border-default)] py-2 first:border-0 text-[12px]">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[var(--text-primary)]">{h.kind}</span>
                    <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{h.at}</span>
                  </div>
                  {h.toStage !== undefined ? <div className="mt-0.5"><span className="text-[11px] text-[var(--text-muted)]">→ </span><span className="text-[11px]" style={{ color: INV_STAGE_META[h.toStage].fg }}>{INV_STAGE_META[h.toStage].label}</span></div> : null}
                  {h.chips !== undefined ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {h.chips.map((c) => <span key={c} className="rounded-full border border-[var(--border-default)] px-2 py-0.5 text-[10.5px] text-[var(--text-secondary)]">{c}</span>)}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {pickerOpen ? (
        <Modal
          title="Which looks most like his smile?"
          onClose={() => { setPickerOpen(false); }}
          className="w-[min(640px,calc(100vw-32px))]"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => { setPickerOpen(false); }}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={() => { setSmile(pickerDraft); setPickerOpen(false); toast({ title: 'Saved', variant: 'success' }); }}>Save</Button>
            </div>
          }
        >
          <InvSmilePicker value={pickerDraft} onChange={setPickerDraft} />
        </Modal>
      ) : null}

      <InvLogModal
        open={logOpen}
        patientName={p.name}
        mode={logMode}
        stage={p.stage}
        {...(topConcern !== null ? { topConcern } : {})}
        onClose={() => { setLogOpen(false); }}
        onSave={(sentence) => { setLogOpen(false); toast({ title: `Logged — ${sentence}`, variant: 'success' }); }}
      />
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">{children}</div>;
}
function H({ children }: { children: React.ReactNode }) {
  return <h3 className="m-0 mb-2.5 text-[13px] font-bold text-[var(--text-primary)]">{children}</h3>;
}
function Figure({ v, l, money }: { v: string; l: string; money?: boolean }) {
  return (
    <div className="text-right">
      <div className={cn('font-[var(--font-mono)] text-[16px] font-bold', money ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>{v}</div>
      <div className="text-[10.5px] text-[var(--text-muted)]">{l}</div>
    </div>
  );
}
function AboutRow({ title, source, children }: { title: string; source: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-[var(--border-default)] py-2.5 first:border-0">
      <div className="flex items-baseline justify-between">
        <span className="text-[12px] font-semibold text-[var(--text-primary)]">{title}</span>
        <span className="text-[10.5px] text-[var(--text-muted)]">{source}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}
function ChipRow({ items, selected, onToggle, single, tag }: { items: string[]; selected: string[]; onToggle: (v: string) => void; single?: boolean; tag?: string }) {
  return (
    <>
      {items.map((it) => {
        const on = selected.includes(it);
        return (
          <button key={it} type="button" onClick={() => { onToggle(it); }}
            className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11.5px]',
              on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] font-semibold text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]')}>
            {single && on && tag === undefined ? '✓ ' : ''}{it}
            {on && tag !== undefined ? <span className="rounded-full bg-[var(--brand-indigo)] px-1.5 py-px text-[9px] font-bold uppercase text-white">{tag}</span> : null}
          </button>
        );
      })}
    </>
  );
}
